/**
 * Cursor Store (Zustand)
 *
 * Manages calendar and capsule cursors for trend visualization.
 */

import _ from 'lodash';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, type StoreInstance, createStore, createStoreInstance } from '@/stores/createStore';
import type { Cursor } from '@/utilities/cursor.constants';

export type CursorStoreEntry = Cursor;

export type DehydratedCursor = Omit<Cursor, 'points' | 'crosshair' | 'anchor' | 'xLabel' | 'deleteIcon'> & {
  // We have to use `unknown` here because Highcharts.SVGElement breaks OpenAPI store schema generation
  crosshair?: unknown;
  anchor?: unknown;
  xLabel?: unknown;
  deleteIcon?: unknown;
};

export interface CursorState {
  calendarCursors: CursorStoreEntry[];
  capsuleCursors: CursorStoreEntry[];
  showValues: boolean;
}

export type CursorDehydratedState = Omit<CursorState, 'calendarCursors' | 'capsuleCursors'> & {
  calendarCursors: DehydratedCursor[];
  capsuleCursors: DehydratedCursor[];
};

export interface CursorActions {
  addCursor: (payload: { xValue: number; capsuleTime: boolean; points: CursorStoreEntry['points'] }) => void;
  renameCursor: (payload: { xValue: number; capsuleTime: boolean; name: string }) => void;
  toggleAnchorLeft: (payload: { xValue: number; capsuleTime: boolean; anchorFromLeft: boolean }) => void;
  adjustAnchorYOffset: (payload: { xValue: number; capsuleTime: boolean; yOffset: number }) => void;
  deleteCursor: (payload: { xValue: number; capsuleTime: boolean }) => void;
  deleteAllCursors: (payload: { capsuleTime: boolean }) => void;
  deleteSelectedCursors: (payload: { capsuleTime: boolean; selectedRegion: { min: number; max: number } }) => void;
  setCursorValueFlags: (show: boolean) => void;
  toggleCursorSelection: (payload: {
    selected: boolean;
    multiSelect: boolean;
    capsuleTime: boolean;
    xValue: number;
  }) => void;
  reset: () => void;
}

export const initialState: CursorState = Object.freeze({
  calendarCursors: [],
  capsuleCursors: [],
  showValues: true,
});

/**
 * Helper to get the cursor state key based on capsule time flag
 */
function getCursorStateKey(capsuleTime: boolean): 'capsuleCursors' | 'calendarCursors' {
  return capsuleTime ? 'capsuleCursors' : 'calendarCursors';
}

/**
 * Determines the next available numerical 'name' for a cursor. The name is generated by finding the largest name
 * in the specified set of cursors and adding 1.
 */
function findNextCursorName(cursors: CursorStoreEntry[]): string {
  const nextName = _.chain(cursors)
    .map((cursor) => (cursor.name ? parseInt(String(cursor.name), 10) : NaN))
    .filter((value) => _.isFinite(value))
    .max()
    .value();

  const newNumber = typeof nextName === 'number' && Number.isFinite(nextName) ? nextName + 1 : 1;
  return newNumber.toString();
}

export const dehydrateCursorState = (state: CursorState): CursorDehydratedState => ({
  calendarCursors: state.calendarCursors.map((item: CursorStoreEntry) => {
    const { points, crosshair, anchor, xLabel, deleteIcon, ...rest } = item;
    return rest;
  }),
  capsuleCursors: state.capsuleCursors.map((item: CursorStoreEntry) => {
    const { points, crosshair, anchor, xLabel, deleteIcon, ...rest } = item;
    return rest;
  }),
  showValues: state.showValues,
});

const cursorActionsFactory: ActionsFactory<CursorState, CursorActions> = (set, get, resetState) => ({
  addCursor: (payload: { xValue: number; capsuleTime: boolean; points: CursorStoreEntry['points'] }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);
      const index = _.findIndex(draft[cursors], ['xValue', payload.xValue]);

      if (index === -1) {
        draft[cursors].push({
          xValue: payload.xValue,
          points: payload.points,
          name: findNextCursorName(draft[cursors]),
          selected: false,
          yOffset: 90,
          xPixel: 0,
          yValues: [],
        });
      } else {
        draft[cursors][index].points = payload.points;
      }
    }),

  renameCursor: (payload: { xValue: number; capsuleTime: boolean; name: string }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);
      const index = _.findIndex(draft[cursors], ['xValue', payload.xValue]);

      if (index > -1) {
        draft[cursors][index].name = payload.name.length > 0 ? payload.name : findNextCursorName(draft[cursors]);
      }
    }),

  toggleAnchorLeft: (payload: { xValue: number; capsuleTime: boolean; anchorFromLeft: boolean }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);
      const index = _.findIndex(draft[cursors], ['xValue', payload.xValue]);

      if (index > -1) {
        draft[cursors][index].anchorFromLeft = !payload.anchorFromLeft;
      }
    }),

  adjustAnchorYOffset: (payload: { xValue: number; capsuleTime: boolean; yOffset: number }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);
      const index = _.findIndex(draft[cursors], ['xValue', payload.xValue]);

      if (index > -1) {
        draft[cursors][index].yOffset = payload.yOffset;
      }
    }),

  deleteCursor: (payload: { xValue: number; capsuleTime: boolean }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);
      const index = _.findIndex(draft[cursors], ['xValue', payload.xValue]);
      if (index > -1) {
        draft[cursors].splice(index, 1);
      }
    }),

  deleteAllCursors: (payload: { capsuleTime: boolean }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);
      draft[cursors] = [];
    }),

  deleteSelectedCursors: (payload: { capsuleTime: boolean; selectedRegion: { min: number; max: number } }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);

      draft[cursors] = draft[cursors].filter(
        (cursor) => cursor.xValue < payload.selectedRegion.min || cursor.xValue > payload.selectedRegion.max,
      );
    }),

  setCursorValueFlags: (show: boolean) =>
    set((draft) => {
      draft.showValues = show;
    }),

  toggleCursorSelection: (payload: { selected: boolean; multiSelect: boolean; capsuleTime: boolean; xValue: number }) =>
    set((draft) => {
      const cursors = getCursorStateKey(payload.capsuleTime);
      if (payload.selected && !payload.multiSelect) {
        // Unselect previously selected unless we're multi-selecting
        draft[cursors].forEach((cursor) => {
          cursor.selected = false;
        });
      }

      const index = _.findIndex(draft[cursors], ['xValue', payload.xValue]);
      if (index > -1) {
        draft[cursors][index].selected = payload.selected;
      }
    }),

  reset: () => {
    resetState();
  },
});

const STORE_NAME = 'Cursor';

const [CursorStoreProvider, useCursorStoreInstance, useCursorSelector] = createStore(
  STORE_NAME,
  initialState,
  cursorActionsFactory,
);

export type LegacyCursorStore = StoreInstance<CursorState, CursorActions> & {
  readonly persistenceLevel: 'WORKSHEET';
  dehydrate: () => CursorDehydratedState;
  rehydrate: (dehydratedState: Partial<CursorDehydratedState>) => void;
  hasCursorsInSelectedRegion: (capsuleTime: boolean, selectedRegion: { min: number; max: number }) => boolean;
  getCursorState: (capsuleTime: boolean) => 'capsuleCursors' | 'calendarCursors';
  findNextCursorName: (cursors: CursorStoreEntry[]) => string;
};

export { CursorStoreProvider };

/**
 * Hook that only re-renders when calendarCursors changes
 */
export const useCalendarCursors = () => useCursorSelector((state) => state.calendarCursors);

/**
 * Hook that only re-renders when capsuleCursors changes
 */
export const useCapsuleCursors = () => useCursorSelector((state) => state.capsuleCursors);

/**
 * Hook that only re-renders when showValues changes
 */
export const useShowCursorValues = () => useCursorSelector((state) => state.showValues);

/**
 * Hook that provides action functions
 */
export const useCursorActions = () => useCursorStoreInstance().actions;

/**
 * Helper hook to check if there are cursors in the selected region
 */
export const useHasCursorsInSelectedRegion = (capsuleTime: boolean, selectedRegion: { min: number; max: number }) => {
  const selector = createSelector(
    (state: CursorState) => (capsuleTime ? state.capsuleCursors : state.calendarCursors),
    (cursors) => cursors.some((cursor) => cursor.xValue >= selectedRegion.min && cursor.xValue <= selectedRegion.max),
  );

  return useCursorSelector(selector);
};

/**
 * Hook to register cursor store for persistence
 */
export const usePersistCursorStore = () => {
  const register = useRegisterPersistedStore();
  const store = useCursorStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqCursorStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateCursorState,
    });
  }, [register, store]);
};

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateCursorStore(initialStateOverrides?: Partial<CursorState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    cursorActionsFactory,
    { storeName: STORE_NAME },
  );
}

/**
 * Creates a new store instance for tests that must assert exact store state or when
 * porting Flux store tests that call actions imperatively.
 *
 * Prefer `<TestStoreProvider>` or `<*StoreProvider>` in tests and validate behavior
 * through UI interactions + DOM assertions whenever possible.
 *
 * NOTE: Only use this function in **test files**.
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestCursorStore(overrides: Partial<CursorState> = {}) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestCursorStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, cursorActionsFactory);
}
